import React, { useEffect, useState } from "react";
import useTheme from "../../../../hooks/useTheme";

import { Divider, Icon, Segment } from "semantic-ui-react"
import { withOpacity } from "../../../../utils/helperFunctions";
import { AppColumn, AppRow } from "../../../UI/Layouts";
import AppText from "../../../UI/Texts/AppTexts";
import { SizedBox } from "../../../UI/Sizes";
import { useResponsive } from "../../../UI/ResponsiveContext";
import { t } from "i18next";
import axios from "axios";
import { FETCH_EVENT_BOOKING_DETAIL } from "../../../../utils/Controller/URLConstants";
import { navigateToJobWithShareableId } from "../../../../utils/navigationFunctions";
import { findChatForJob } from "../../../../utils/helperFunctions";
import { ensureRelationshipChat } from "../../../../utils/chatApi";
import { getUnreadCountForJob } from "../../../Chats/Messages/messageHelpers";
import CandidateDetailMainContent from "./CandidateDetailMainContent";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { MESSAGES_PATH } from "../../../../utils/Constants/Paths";


const CandidateDetailPage = ({ candidateData, closeModal, job, showToast, fromCondidateApplyForm = false, onCandidateChange }) => {
    const theme = useTheme();
    const coreConstants = useSelector((state) => state.core.constants);
    const { currentUser , profile, chats} = useSelector((state) => state.auth);
    const { jobs } = useSelector((state) => state.jobs);


    const { isMobile } = useResponsive();
    const navigation = useNavigate();

    const dashboardJobs = jobs?.dashboardJobs ?? [];
    const currentJob = job;

    const [eventDetailData, setEventDetail] = useState(null)

    const candidateJobId = candidateData?.jobDetails?.jobId ?? candidateData?.jobId;
    const existingAppliedJobData = dashboardJobs?.find((job) => job.jobId === candidateJobId);

    const fullJobData = currentJob?.jobId === candidateJobId ? currentJob : existingAppliedJobData;

    // Fall back to candidateData.jobDetails (miniJobDetails) when the full job isn't loaded,
    // mapping its flat fields to the nested structure CandidateDetailMainContent expects.
    const hw = candidateData?.jobDetails?.healthcareWorkplace ?? {};
    const currentAppliedJobData = fullJobData ?? (candidateData?.jobDetails ? {
        ...candidateData.jobDetails,
        healthcareWorkplaceDetails: {
            name: hw.name ?? candidateData.jobDetails.workplaceName ?? "",
            logoUrl: hw.logoUrl ?? candidateData.jobDetails.workplaceLogo ?? "",
            city: hw.city ?? candidateData.jobDetails.workplaceCity ?? "",
            municipality: hw.municipality ?? candidateData.jobDetails.workplaceMunicipality ?? "",
            workPlaceId: hw.id ?? candidateData.jobDetails.healthcareWorkplaceId ?? "",
        },
        workplaceDetails: {
            name: hw.name ?? candidateData.jobDetails.workplaceName ?? "",
            logoUrl: hw.logoUrl ?? candidateData.jobDetails.workplaceLogo ?? "",
            workPlaceId: candidateData.jobDetails.workplaceId ?? "",
        },
    } : null);



    async function fetchEventDetails() {

        try {
            const response = await axios.post(FETCH_EVENT_BOOKING_DETAIL, {
                eventId: candidateData?.eventBooking,
                token: currentUser?.stsTokenManager?.accessToken,
            });

            if (response.data && response.data?.result) {
                setEventDetail(response.data.result)
            }
        } catch (error) {
            console.log(error);
        }
    }


    useEffect(() => {
        if (!eventDetailData) {
            fetchEventDetails()
        }
    }, [eventDetailData])


    const candidateJobChat = findChatForJob(chats, {
        jobId: candidateJobId,
        relationshipId: candidateData?.relationshipId,
        workplaceId: candidateData?.workplaceId ?? candidateData?.tenantId ?? candidateData?.jobDetails?.workplaceId,
    });

    const myProfile = candidateJobChat?.profilesData?.[currentUser?.uid];
    const unreadCount = getUnreadCountForJob(myProfile, candidateJobId);

    const navigateToMessages = async () => {
        closeModal();
        if (candidateJobChat) {
            navigation(`${MESSAGES_PATH}/${candidateJobChat.chatId}?thread=${candidateJobId}`);
            return;
        }
        // The synced chat list only contains chats this admin already
        // belongs to, so a candidate they've never messaged won't have a
        // candidateJobChat yet even though the relationship (and its chat)
        // may already exist. Ensure it — adding this admin if authorized —
        // instead of falling back to the unrelated generic inbox.
        const relationshipId = candidateData?.relationshipId;
        if (relationshipId) {
            const chat = await ensureRelationshipChat(relationshipId);
            if (chat?.chatId) {
                navigation(`${MESSAGES_PATH}/${chat.chatId}`);
                return;
            }
            showToast(t("something_went_wrong"), { type: "error" });
            return;
        }
        navigation(MESSAGES_PATH);
    };

    const prepareAndNavigateToJob = async () => {
        navigateToJobWithShareableId(currentAppliedJobData);
    };

    let isLoading = !currentAppliedJobData;

    const applicationContent = (<AppColumn
        style={{
            height: "100%",
            maxHeight: !isMobile && '90vh',
        }}
        crossAxisAlignment={"stretch"}>
        <AppColumn>
            <AppRow crossAxisAlignment={'center'} style={{
                paddingLeft: theme.spacing.contentElementPadding,
                paddingRight: theme.spacing.contentElementPadding,
                paddingTop: theme.spacing.elementPadding,
            }}>
                <Icon name="close" style={{ cursor: 'pointer' }} onClick={() => {
                    if (fromCondidateApplyForm) {
                        navigation(-1);
                    } else {
                        closeModal();
                    }
                }} />

                <SizedBox width={theme.spacing.elementPadding} />

                <AppText.BodyText fontWeight={600} margins={{ marginBottom: '0px' }} color={theme.colors.dark} style={{
                    flex: 1,
                }}>
                    {fromCondidateApplyForm ? t('your_application') : t('view_application')}
                </AppText.BodyText>

                <AppText.Caption
                    cursor='pointer'
                    onClick={prepareAndNavigateToJob}
                    textDecoration={'underline'}>
                    {!isMobile && 'Se jobbet'}
                    <Icon name="external alternate" size='small' />
                </AppText.Caption>

            </AppRow>
            <SizedBox height={theme.spacing.elementPadding} />
            <Divider fitted />
        </AppColumn>

        {/* Messages button */}
        <div
            onClick={navigateToMessages}
            style={{
                margin: `${theme.spacing.elementPadding} ${theme.spacing.contentElementPadding}`,
                padding: `${theme.spacing.elementPaddingSmall} ${theme.spacing.elementPadding}`,
                borderRadius: theme.radius.small,
                backgroundColor: unreadCount > 0
                    ? withOpacity(theme.primaryColor, "0.08")
                    : theme.colors.lightGrey,
                border: `1px solid ${unreadCount > 0 ? withOpacity(theme.primaryColor, "0.3") : theme.colors.lightGrey}`,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: theme.spacing.elementPaddingSmall,
            }}
        >
            <Icon
                name="comment outline"
                style={{
                    color: theme.primaryColor,
                    fontSize: "1.1rem",
                    margin: 0,
                }}
            />
            <AppText.Caption fontWeight={500} color={theme.primaryColor}>
                {t('messages')}
            </AppText.Caption>
            {unreadCount > 0 && (
                <div
                    style={{
                        marginLeft: "auto",
                        backgroundColor: theme.primaryColor,
                        color: theme.colors.white,
                        borderRadius: "10px",
                        padding: "1px 8px",
                        fontSize: "12px",
                        fontWeight: 600,
                        minWidth: "20px",
                        textAlign: "center",
                    }}
                >
                    {unreadCount}
                </div>
            )}
            {unreadCount === 0 && (
                <Icon
                    name="chevron right"
                    style={{
                        marginLeft: "auto",
                        color: theme.primaryColor,
                        fontSize: "0.8rem",
                    }}
                />
            )}
        </div>

        {isLoading ? <Segment
            basic
            loading={isLoading}
            placeholder={isLoading}
            style={isLoading ? {
                height: "50vh",
            } : {}}
        /> : <CandidateDetailMainContent
            currentUser={currentUser}
            candidateData={candidateData}
            jobData={currentAppliedJobData}
            profile={profile}
            coreConstants={coreConstants}
            eventDetailData={eventDetailData}
            fromCondidateApplyForm={fromCondidateApplyForm}
            onCandidateChange={(jobCandidate) => {
                if (onCandidateChange) onCandidateChange(jobCandidate);
            }}
            onClose={() => {
                closeModal();
            }}
            showToast={showToast}
        />}
        


    </AppColumn>)

    return applicationContent;

}


export default CandidateDetailPage;